import { dependentKeyCompat } from '@ember/object/compat';
import { map, not } from '@ember/object/computed';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';

import Model, { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { TrackedObject } from 'tracked-built-ins';

import {
  buildValidations,
  validator,
} from 'umg-ember-backend/utils/validations';

const fontFamiliesToLoad = map(
  'model.page.mlp.options.fontFamiliesToLoad',
  (fontFamily) => {
    return fontFamily.familyWithFallback;
  },
);

const Validations = buildValidations({
  ['options.fontFamily']: validator('inclusion', {
    in: fontFamiliesToLoad,
    allowBlank: true,
    disabled: not('model.page.mlp.isV2'),
  }),
  ['options.headlineFontFamily']: validator('inclusion', {
    in: fontFamiliesToLoad,
    allowBlank: true,
    disabled: not('model.page.mlp.isV2'),
  }),
});

const NON_DUPLICATABLE_TYPES = ['newsletter', 'link', 'contest'];

export function isContentItemType(type, contentItemType) {
  const typeMap = {
    module: [
      'hero-header',
      'html',
      'mlp-add-to-wallet',
      'mlp-countdown',
      'mlp-download-link-form',
      'mlp-live-event-feed',
      'mlp-news-feed',
      'mlp-product-feed',
      'mlp-stage-plus-stream',
      'mlp-text',
      'mlp-voucher',
      'mlp-zatap',
      'newsletter',
      'spacer',
    ],
    placeholder: ['mlp-column', 'mlp-group', 'mlp-row', 'mlp-slider'],
    layout: ['mlp-column', 'mlp-group', 'mlp-row', 'mlp-slider'],
  };

  return typeMap[type].includes(contentItemType);
}

export const settings = {
  copyableOptions(record) {
    return {
      skipRecord: NON_DUPLICATABLE_TYPES.includes(record.type),
      overwrite: {
        options: {
          ...record.options,
        },
        contentItemId: record.isNotDefault ? null : record.contentItemId,
      },
      relationsToCopy: ['children', 'entity'],
    };
  },
};

export default class ContentItemModel extends Model.extend(Validations) {
  // Services
  @service intl;

  // Attributes
  @attr('string') collectionType;
  @attr('string') contentItemId;
  @attr('string') headline;
  @attr('json', {
    defaultValue() {
      return new TrackedObject({});
    },
  })
  options;
  @attr('string') type;
  @attr('boolean', { defaultValue: true }) visible;

  // Relation
  @belongsTo('asset', { async: true, inverse: null }) backgroundImage;
  @belongsTo('asset', { async: true, inverse: null }) backgroundVideo;
  @belongsTo('asset', { async: true, inverse: null }) backgroundImageMobile;
  @belongsTo('asset', { async: true, inverse: null }) backgroundVideoMobile;
  @hasMany('content-item', { async: false, inverse: 'parent' }) children;
  @belongsTo('content-item', { async: true, inverse: 'children' }) parent;
  @belongsTo('mlp-module', {
    async: false,
    inverse: 'contentItem',
    polymorphic: true,
  })
  entity;
  @belongsTo('mlp-page', { async: false, inverse: 'contentItems' }) page;

  // Getter and setter
  get isNotDefault() {
    return this.isModule || this.isPlaceholder || this.isLayoutModule;
  }

  get isDefault() {
    return !this.isNotDefault;
  }

  get displayHeadline() {
    if (this.entity?.displayHeadline) {
      return this.entity?.displayHeadline;
    }

    if (this.options?.headline) {
      return this.options.headline;
    }

    if (this.headline) {
      return this.headline;
    }

    return this.intl.t(`models.${this.type}`);
  }

  @dependentKeyCompat
  get isModule() {
    return isContentItemType('module', this.type);
  }

  @dependentKeyCompat
  get isPlaceholder() {
    return isContentItemType('placeholder', this.type);
  }

  @dependentKeyCompat
  get isLayoutModule() {
    return isContentItemType('layout', this.type);
  }

  get isParentVisible() {
    if (!isPresent(this.parent?.get('isVisible'))) {
      return true;
    }

    return this.parent?.get('isVisible');
  }

  get isVisible() {
    if (!this.visible) {
      return false;
    }

    return this.isParentVisible;
  }

  get isDuplicationDisabled() {
    return NON_DUPLICATABLE_TYPES.includes(this.type);
  }

  // Functions
  resetOptions() {
    for (const key in this.options) {
      delete this.options[key];
    }

    this.backgroundImage = null;
    this.backgroundVideo = null;
  }

  // ContentItems don't have a cmsModified so we the mlp.cmsModified
  get cmsModified() {
    return this.parent?.get('cmsModified') ?? this.page?.cmsModified;
  }
}
